import { Box, Divider, Grid, Typography, Button, CircularProgress, Stack } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Table from '@mui/material/Table';
import moment from 'moment';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import { useParams } from 'react-router-dom';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Icon } from '@iconify/react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import useMetaData from '../../hooks/useMetaData';
import { META_DATA_MODULES, META_DATA_SUB_MODULES } from '../../utils/constants';
import { handleType } from '../Payments';
import GridLabels from '../VehicleTabs/GridLabels';
import { billingService, vehicleService } from '../../services/vehicles';
import { currencyFormat } from '../../utils/string';
import { Link } from '../../components';
import useSnackbarNotifications from '../../hooks/useSnackbarNotifications';

const PricingPurchaseAgreement = () => {
  // use hooks
  const { reservationId } = useParams<{ reservationId: string }>();
  // const metaData = useMetaData(META_DATA_MODULES.v360Page, META_DATA_SUB_MODULES.pricingPurchaseAgreement);

  // local states
  const [purchaseData, setPurchaseData] = useState<any>(null);
  const [billingPriceFlag, setBillingPriceFlag] = useState<boolean>(false);
  const [billingPurchaseData, setBillingPurchaseData] = useState<any>();
  let documentsList = [];
  const vehicle = useSelector((state: RootState) => state.vehicle.data);
  const { showErrorSnackbar } = useSnackbarNotifications();

  const sortedPurchaseData = useMemo(() => {
    if (purchaseData) {
      const orderData = purchaseData.orderLines;
      const sorted = orderData.sort((a: any, b: any) => b.amount - a.amount);
      return sorted;
    }
    return null;
  }, [purchaseData]);

  const sortedbillingPurchaseData = useMemo(() => {
    if (billingPurchaseData && billingPurchaseData.transactionLineItems) {
      const orderBillingData: Array<any> = billingPurchaseData.transactionLineItems;
      const lineItemTrack: any = {};
      orderBillingData.forEach((lineItem) => {
        const subCat = lineItem.contractRollupSubCatagory;
        lineItemTrack[subCat] = lineItemTrack[subCat] ? lineItemTrack[subCat] + lineItem.amount : lineItem.amount;
      });
      const purchaseItems: Array<any> = [];
      Object.entries(lineItemTrack).forEach((line) => {
        purchaseItems.push({ name: line[0], amount: line[1] });
      });
      const sorted: Array<any> = purchaseItems.sort((a: any, b: any) => b.amount - a.amount);
      return sorted;
    }
    return null;
  }, [billingPurchaseData]);

  // constants
  // const purchaseAgreementFields = metaData?.data?.purchaseAgreement?.rows?.map((row: any) => {
  //   const value = handleType(row.type, purchaseData, row);
  //   return {
  //     label: row?.name,
  //     title: value ?? '--',
  //   };
  // });

  // fetching record
  const fetchPurchaseData = useCallback(async () => {
    try {
      const resp = await vehicleService({
        method: 'GET',
        url: `${reservationId}/purchasePlan`,
      });
      setPurchaseData(resp?.data?.data);
      if (resp?.data?.data && resp?.data?.data.dotEnabled) {
        setBillingPriceFlag(true);
        const billingResp = await billingService({
          method: 'POST',
          data: {
            orderId: vehicle.orderId,
            summary: false,
            version: 0,
          },
          url: `getTransaction`,
        });
        setBillingPurchaseData(billingResp?.data?.searchTransactions);
      }
    } catch (error) {
      console.log(error);
    }
  }, [reservationId, vehicle]);

  const pdfClickHandler = async () => {
    const purchaseAgreementResp = await billingService({
      method: 'POST',
      data: {
        orderId: vehicle.orderId,
        docTypes: ['PURCHASE_AGREEMENT', 'PURCHASE_AGREEMENT_SIGNED'],
      },
      url: `getDocumentsV2`,
    });
    documentsList = purchaseAgreementResp.data;
    const respFileData = documentsList[1].fileData ? documentsList[1].fileData : documentsList[0].fileData;
    if (respFileData) {
      const byteChar = atob(respFileData);
      const byteNum = new Array(byteChar.length);
      for (let i = 0; i < byteChar.length; i++) {
        byteNum[i] = byteChar.charCodeAt(i);
      }
      const byteArr = new Uint8Array(byteNum);
      const file = new Blob([byteArr], { type: 'application/pdf;base64' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    } else {
      showErrorSnackbar({ message: 'No Purchase Agreements present at the moment.' });
    }
  };

  // lifecycle hooks
  useEffect(() => {
    fetchPurchaseData();
  }, [fetchPurchaseData]);

  if (!purchaseData || (billingPriceFlag && !billingPurchaseData))
    return (
      <Box sx={{ position: 'relative', minHeight: '250px' }}>
        <CircularProgress
          color="inherit"
          size={18}
          sx={{ position: 'absolute', left: '0', right: '0', top: '50%', margin: '0 auto' }}
        />
      </Box>
    );
  return (
    <Box sx={{ width: '100%' }}>
      <Grid>
        <Typography sx={{ fontSize: '20px', fontWeight: '500', lineHeight: '24px' }}>Pricing Summary</Typography>

        {/* - - Pricing - - - - - - - */}
        {!billingPriceFlag && (
          <TableContainer
            component={Paper}
            sx={{ my: '10px', border: '1px solid #e3e3e3', borderRadius: '4px', borderBottom: '0', boxShadow: 'none' }}
          >
            <Table aria-label="simple table">
              {/* <TableHead>
                <TableRow>
                  <TableCell colSpan={2} sx={{ pt: '3px', pb: '3px', pl: '15px', pr: '15px', background: '#F5F5F5' }}>
                    <Typography fontSize={14} fontWeight={500}>
                      {metaData.data.pricing.heading}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead> */}
              <TableBody>
                {sortedPurchaseData?.map((row: { line: string; amount: number | string }) => {
                  // const value = handleType(row.type, purchaseData, row);
                  if (!row || row?.amount === '') return null;
                  return (
                    <TableRow>
                      <TableCell
                        component="td"
                        scope="row"
                        sx={{
                          padding: '7px 10px',
                          // fontSize: row.id === 'totalDue' ? '12px' : '14px',
                          fontSize: '14px',
                          fontWeight: 500,
                          // background: row.id === 'totalDue' ? '#f2f2f2' : '#fff',
                          background: '#fff',
                        }}
                      >
                        <Typography fontSize={12}>{row?.line}</Typography>
                      </TableCell>
                      <TableCell
                        component="td"
                        scope="row"
                        sx={{
                          padding: '7px 10px',
                          fontSize: '12px',
                          // background: row.id === 'totalDue' ? '#f2f2f2' : '#fff',
                          background: '#fff',
                        }}
                        align="right"
                      >
                        {currencyFormat(row?.amount, 'USD', 'US')}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell
                    component="td"
                    scope="row"
                    sx={{
                      padding: '7px 10px',
                      fontSize: '12px',
                      fontWeight: 500,
                      background: '#f2f2f2',
                    }}
                  >
                    <Typography fontWeight={500} fontSize={14}>
                      Total Price
                    </Typography>
                  </TableCell>
                  <TableCell
                    component="td"
                    scope="row"
                    sx={{
                      padding: '7px 10px',
                      fontSize: '12px',
                      background: '#f2f2f2',
                    }}
                    align="right"
                  >
                    <Typography fontWeight={500} fontSize={14}>
                      {currencyFormat(purchaseData?.vehiclePrice, 'USD', 'US')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* - - - Price Agreement from Billing - - - - - */}
        {billingPriceFlag && (
          <TableContainer
            component={Paper}
            sx={{ my: '10px', border: '1px solid #e3e3e3', borderRadius: '4px', borderBottom: '0', boxShadow: 'none' }}
          >
            <Table aria-label="simple table">
              <TableBody>
                {sortedbillingPurchaseData?.map((row: any) => {
                  // const value = handleType(row.type, purchaseData, row);
                  if (!row || row?.amount === '') return null;
                  return (
                    <TableRow>
                      <TableCell
                        component="td"
                        scope="row"
                        sx={{
                          padding: '7px 10px',
                          // fontSize: row.id === 'totalDue' ? '12px' : '14px',
                          fontSize: '14px',
                          fontWeight: 500,
                          // background: row.id === 'totalDue' ? '#f2f2f2' : '#fff',
                          background: '#fff',
                        }}
                      >
                        <Typography fontSize={12}>{row?.name}</Typography>
                      </TableCell>
                      <TableCell
                        component="td"
                        scope="row"
                        sx={{
                          padding: '7px 10px',
                          fontSize: '12px',
                          // background: row.id === 'totalDue' ? '#f2f2f2' : '#fff',
                          background: '#fff',
                        }}
                        align="right"
                      >
                        {currencyFormat(row?.amount, 'USD', 'US')}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell
                    component="td"
                    scope="row"
                    sx={{
                      padding: '7px 10px',
                      fontSize: '12px',
                      fontWeight: 500,
                      background: '#f2f2f2',
                    }}
                  >
                    <Typography fontWeight={500} fontSize={14}>
                      Total Price
                    </Typography>
                  </TableCell>
                  <TableCell
                    component="td"
                    scope="row"
                    sx={{
                      padding: '7px 10px',
                      fontSize: '12px',
                      background: '#f2f2f2',
                    }}
                    align="right"
                  >
                    <Typography fontWeight={500} fontSize={14}>
                      {currencyFormat(billingPurchaseData?.transactionSummary?.amounts?.totalIncludingTax, 'USD', 'US')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* - - Purchase Agreement - - - - - */}
        <Box sx={{ width: '100%', my: 2 }}>
          <Typography fontSize={14} mb={1} fontWeight={500}>
            Purchase Agreement
          </Typography>
          <Divider sx={{ borderColor: '#e3e3e3', borderStyle: 'dashed' }} />
          {/* <Grid container spacing={2} mt={0}>
            <GridLabels
              labels={[{ label: 'Status', title: purchaseData?.purchasePlanStatus ?? '--', type: 'Status' }]}
              labelSx={{ fontSize: '14px', fontWeight: '400', color: '#717171' }}
              titleSx={{ fontSize: '16px', fontWeight: '500', color: '#3E3E3E' }}
            />
          </Grid> */}
        </Box>
        <Stack>
          {/* <Typography
            sx={{
              fontSize: '14px',
              fontWeight: '400',
              color: '#717171',
              paddingBottom: '8px',
              // marginTop: '20px',
            }}
          >
            License Details:
          </Typography> */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              border: '1px solid #E9E9E9',
              padding: '12px 13px 9px 17px',
              borderRadius: '6px',
              paddingLeft: '62px',
              position: 'relative',
            }}
          >
            <Box sx={{ position: 'absolute', left: '10px' }}>
              <Icon
                icon="bi:file-pdf-fill"
                width="40"
                color="#C2B299"
                style={{ cursor: 'pointer' }}
                onClick={pdfClickHandler}
              />
            </Box>
            <Grid container spacing={2} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              <Grid item xs={3}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#717171',
                    paddingBottom: '1px',
                  }}
                >
                  Generated On
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#3E3E3E',
                  }}
                >
                  {billingPriceFlag && billingPurchaseData?.transactionSummary?.transactionDates?.contractGeneratedDate
                    ? moment(billingPurchaseData?.transactionSummary?.transactionDates?.contractGeneratedDate)?.format(
                        'MMM D, YYYY'
                      )
                    : ''}
                  {billingPriceFlag &&
                  (!billingPurchaseData?.transactionSummary?.transactionDates?.contractGeneratedDate ||
                    billingPurchaseData?.transactionSummary?.transactionDates?.contractGeneratedDate === '')
                    ? '--'
                    : ''}
                  {!billingPriceFlag &&
                  (!purchaseData?.pa?.purchaseAgreementGeneratedDate ||
                    purchaseData?.pa?.purchaseAgreementGeneratedDate === '')
                    ? '--'
                    : ''}
                  {!billingPriceFlag &&
                  purchaseData?.pa?.purchaseAgreementGeneratedDate &&
                  purchaseData?.pa?.purchaseAgreementGeneratedDate !== ''
                    ? moment(purchaseData?.pa?.purchaseAgreementGeneratedDate)?.format('MMM D, YYYY')
                    : ''}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#717171',
                    paddingBottom: '1px',
                  }}
                >
                  Sent on
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#3E3E3E',
                  }}
                >
                  {purchaseData?.pa?.purchaseAgreementApprovedSent &&
                  purchaseData?.pa?.purchaseAgreementApprovedSent !== ''
                    ? moment(purchaseData?.pa?.purchaseAgreementApprovedSent)?.format('MMM D, YYYY')
                    : '--'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#717171',
                    paddingBottom: '1px',
                  }}
                >
                  Signed on
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#3E3E3E',
                  }}
                >
                  {billingPriceFlag && billingPurchaseData?.transactionSummary?.transactionDates?.contractSignedDate
                    ? moment(billingPurchaseData?.transactionSummary?.transactionDates?.contractSignedDate)?.format(
                        'MMM D, YYYY'
                      )
                    : ''}
                  {billingPriceFlag &&
                  (!billingPurchaseData?.transactionSummary?.transactionDates?.contractSignedDate ||
                    billingPurchaseData?.transactionSummary?.transactionDates?.contractSignedDate === '')
                    ? '--'
                    : ''}
                  {!billingPriceFlag &&
                  (!purchaseData?.pa?.contractSignedDate || purchaseData?.pa?.contractSignedDate === '')
                    ? '--'
                    : ''}
                  {!billingPriceFlag &&
                  purchaseData?.pa?.purchaseAgreementSignedDate &&
                  purchaseData?.pa?.purchaseAgreementSignedDate !== ''
                    ? moment(purchaseData?.pa?.purchaseAgreementSignedDate)?.format('MMM D, YYYY')
                    : ''}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: '400',
                    color: '#717171',
                    paddingBottom: '1px',
                  }}
                >
                  Rejected on
                </Typography>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#3E3E3E',
                  }}
                >
                  {purchaseData?.pa?.purchaseAgreementRejectedDate &&
                  purchaseData?.pa?.purchaseAgreementRejectedDate !== ''
                    ? moment(purchaseData?.pa?.purchaseAgreementRejectedDate)?.format('MMM D, YYYY')
                    : '--'}
                </Typography>
              </Grid>
              {/* <Grid item xs={3} sx={{ textAlign: 'right' }}>
                <Link
                  to="/"
                  onClick={(e) => e.preventDefault()}
                  sx={{ fontSize: 14, fontWeight: 400, color: '#456BD9' }}
                >
                  View Document
                </Link>
              </Grid> */}
            </Grid>
          </Box>
        </Stack>
        {/* <Grid container spacing={2} mt={1} sx={{ marginLeft: '0px', width: '100%' }}>
          <Box
            sx={{
              '&:first-child': { borderLeft: 0 },
              '&:last-child': { borderBottom: 0 },
              padding: '8px 10px',
              background: '#f2f2f2',
              width: '100%',
              my: '20px',
            }}
          >
            <Typography color="#1B1B1B" fontSize={14} fontWeight={500}>
              {metaData.data.purchaseAgreement.heading}
            </Typography>
          </Box>
          <GridLabels labels={purchaseAgreementFields} />
        </Grid> */}

        {/* - - Signed Purchase Agreement - - - - - */}
        {/* <TableContainer component={Paper} sx={{ my: '30px', borderRadius: '0', boxShadow: 'none' }}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableBody>
              <TableRow>
                <TableCell
                  component="td"
                  scope="row"
                  sx={{ pt: '15px', pb: '15px', pl: '15px', width: '50%' }}
                  colSpan={2}
                >
                  <Typography component="label" fontSize={16} fontWeight={500} color="#1F1F1F">
                    Signed Purchase Agreement
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Box
                        sx={{
                          border: '1px dashed #7B9BF8',
                          background: '#F4F5FB',
                          minHeight: '93px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          my: '10px',
                        }}
                      >
                        <Icon icon="carbon:document" color="#456BD9" fontSize={18} />
                        <Typography fontSize={12} color="#717171" mt={0.8}>
                          PA-001
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box
                        sx={{
                          border: '1px dashed #7B9BF8',
                          background: '#F4F5FB',
                          minHeight: '93px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          my: '10px',
                        }}
                      >
                        <Icon icon="carbon:document" color="#456BD9" fontSize={18} />
                        <Typography fontSize={12} color="#717171" mt={0.8}>
                          PA-001
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box
                        sx={{
                          border: '1px dashed #7B9BF8',
                          background: '#F4F5FB',
                          minHeight: '93px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          my: '10px',
                        }}
                      >
                        <Icon icon="carbon:document" color="#456BD9" fontSize={18} />
                        <Typography fontSize={12} color="#717171" mt={0.8}>
                          PA-001
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer> */}
      </Grid>
    </Box>
  );
};

export default PricingPurchaseAgreement;
